/**
 * Product-list → GA4 `AnalyticsListItem` mapper.
 *
 * ─── PRICE UNITS: PENCE. NON-NEGOTIABLE. ────────────────────────────────────
 * `lib/analytics.ts` takes integer PENCE at every boundary and divides by 100
 * internally. Handing it pounds is a silent 100× revenue error in GA4 that no
 * cross-module test can catch, so the conversion lives in exactly one place:
 * here. This module intentionally does NO division.
 *
 * The price reported is the one the CUSTOMER SEES — the card's "From £X",
 * i.e. `variantPriceRange().min` falling back to the base price. Those values
 * are already uplift-adjusted (`/api/products` and the SSR seed both run
 * `adjustPricePence` before the products reach the client), which is the same
 * source `trackAddToCart` uses in `ProductsClient`, so `view_item_list`,
 * `select_item` and `add_to_cart` cannot disagree on what a product costs.
 */

import type { AnalyticsListItem } from "@/lib/analytics";
import { variantPriceRange } from "@/lib/product-pricing";

export interface ListAnalyticsProduct {
  id: string;
  title: string;
  pricePence: number | null;
  variants?: { pricePence: number | null }[];
}

/** Customer-facing "from" price in PENCE, or null for enquire-only products. */
export function listItemPricePence(p: ListAnalyticsProduct): number | null {
  const { min } = variantPriceRange(p.pricePence, p.variants ?? []);
  return min ?? p.pricePence ?? null;
}

/**
 * Map products to `AnalyticsListItem`s. `startIndex` offsets the 0-based
 * `index` so a single card can report its real position within the list.
 */
export function toAnalyticsListItems(
  products: ListAnalyticsProduct[],
  startIndex = 0
): AnalyticsListItem[] {
  return products.map((p, i) => ({
    id: p.id,
    name: p.title,
    pricePence: listItemPricePence(p),
    index: startIndex + i,
  }));
}
